import type { FilterOptionType } from '@providers/FilterProvider';
import type { MaybeArrayItem } from '@types';
import { toItemArray } from '@utils';

import type { FilterOptionProps } from '../FilterOption';
import FilterOption from '../FilterOption';
import { filterOptionItemSettings } from '../FilterOptionItem';
import type {
  ChildProps as ListItemChildProps,
  FilterOptionListItemProps as ListItemProps,
} from './Item';
import FilterOptionListItem from './Item';

export type FilterOptionListProps<FilterOption extends FilterOptionType> = {
  option: FilterOption;
  filterOptionProps?: Partial<FilterOptionProps>;
  filterOptionItemProps: (
    optionItem: MaybeArrayItem<FilterOption['values']>
  ) => Pick<ListItemProps, 'value' | 'docCount' | 'label'> & { items?: FilterOption['values'] };
};

const FilterOptionList = <FilterOption extends FilterOptionType>({
  option,
  filterOptionProps,
  filterOptionItemProps,
}: FilterOptionListProps<FilterOption>) => {
  const itemSettings = filterOptionItemSettings(option);
  const getFilterOptionItemsProps = (
    filterOptions: FilterOption['values']
  ): Array<Pick<ListItemChildProps, 'value' | 'docCount' | 'items' | 'handle'>> => {
    return toItemArray(filterOptions).map((optionItem) => {
      const optionItemProps = filterOptionItemProps(optionItem);

      const optionItemSubItems = optionItemProps?.items
        ? getFilterOptionItemsProps(optionItemProps.items)
        : undefined;

      return {
        ...optionItemProps,
        items: optionItemSubItems,
        handle: optionItem.handle,
      };
    });
  };

  const renderFilterOptionItem = (
    itemProps: Pick<ListItemChildProps, 'value' | 'docCount' | 'items'>
  ) => (
    <FilterOptionListItem
      key={itemProps.value}
      settings={itemSettings}
      option={option}
      {...itemProps}
      {...filterOptionProps}
    >
      {itemProps.items?.map(renderFilterOptionItem)}
    </FilterOptionListItem>
  );

  return (
    <FilterOption option={option} {...filterOptionProps}>
      {({ filterOptions }) => {
        const itemsProps = getFilterOptionItemsProps(toItemArray(filterOptions));
        return itemsProps.map(renderFilterOptionItem);
      }}
    </FilterOption>
  );
};

export default FilterOptionList;
